import React, { useState, useEffect } from "react";
import { CheckCircle2, XCircle, Sparkles, Users } from "lucide-react";
import { API_URL } from "../config";
import { useAuth } from "../AuthContext";

export default function CaseQuizPlayer({ caseId }) {
  const { isLoggedIn, token } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [checked, setChecked] = useState(false); // квиз есть, но ещё не проверяли (избегаем лишнего запроса, если разбора нет)
  const [stats, setStats] = useState(null);
  const [started, setStarted] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [answerResult, setAnswerResult] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/cases/${caseId}/quiz/stats`)
      .then(r => r.ok ? r.json() : null)
      .then(setStats)
      .catch(() => {});
  }, [caseId]);

  useEffect(() => {
    if (!isLoggedIn || !token) { setChecked(true); return; }
    fetch(`${API_URL}/cases/${caseId}/quiz`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(setQuiz)
      .finally(() => setChecked(true));
  }, [caseId, isLoggedIn, token]);

  if (!checked || !quiz) return null; // нет разбора для этого случая — секцию не показываем

  const step = quiz.steps[stepIdx];
  const isLastStep = stepIdx === quiz.steps.length - 1;

  const submitAnswer = async () => {
    if (selectedOption == null) return;
    const res = await fetch(`${API_URL}/cases/${caseId}/quiz/steps/${step.id}/answer`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ option_id: selectedOption }),
    });
    if (!res.ok) return;
    const result = await res.json();
    setAnswerResult(result);
    if (result.is_correct) setScore(s => s + 1);
  };

  const nextStep = async () => {
    if (isLastStep) {
      await fetch(`${API_URL}/cases/${caseId}/quiz/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ score, total_steps: quiz.steps.length }),
      }).catch(() => {});
      setFinished(true);
      fetch(`${API_URL}/cases/${caseId}/quiz/stats`).then(r => r.ok ? r.json() : null).then(setStats).catch(() => {});
    } else {
      setStepIdx(i => i + 1);
      setSelectedOption(null);
      setAnswerResult(null);
    }
  };

  const restart = () => {
    setStarted(false);
    setStepIdx(0);
    setSelectedOption(null);
    setAnswerResult(null);
    setScore(0);
    setFinished(false);
  };

  return (
    <div className="card" style={{ marginTop: 20, background: "#fff1f6", border: "1.5px solid #ffc2d9" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#e5005b", fontSize: "1rem", marginBottom: 6 }}>
        <Sparkles size={18} strokeWidth={2.2} /> Интерактивный разбор
      </div>

      {!started && !finished && (
        <div>
          <p style={{ color: "#9c0040", fontSize: "0.92rem" }}>
            Пройдите пошаговый разбор случая — {quiz.steps.length} {quiz.steps.length === 1 ? "вопрос" : "вопроса"} с обратной связью.
          </p>
          {stats && stats.attempts_count > 0 && (
            <p style={{ color: "#b0476f", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 5 }}>
              <Users size={13} strokeWidth={2.2} /> Уже прошли: {stats.attempts_count}
            </p>
          )}
          <button onClick={() => setStarted(true)} style={{ background: "#e5005b" }}>Пройти разбор</button>
        </div>
      )}

      {started && !finished && step && (
        <div>
          <div style={{ color: "#b0476f", fontSize: "0.8rem", marginBottom: 6 }}>
            Шаг {stepIdx + 1} из {quiz.steps.length}
          </div>
          <div style={{ color: "#9c0040", fontWeight: 600, fontSize: "0.98rem", marginBottom: 12 }}>
            {step.question_text}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            {step.options.map(opt => {
              const isChosen = selectedOption === opt.id;
              const isCorrectOpt = answerResult && opt.id === answerResult.correct_option_id;
              const isWrongChosen = answerResult && isChosen && !answerResult.is_correct;
              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={!!answerResult}
                  onClick={() => setSelectedOption(opt.id)}
                  style={{
                    textAlign: "left", padding: "9px 12px", borderRadius: 8, cursor: answerResult ? "default" : "pointer",
                    border: isChosen ? "2px solid #e5005b" : "1.5px solid #ffc2d9",
                    background: isCorrectOpt ? "#e6f5ec" : isWrongChosen ? "#fbe9e7" : "#fff",
                    color: "#333", fontSize: "0.9rem", marginTop: 0,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                  }}
                >
                  {opt.option_text}
                  {isCorrectOpt && <CheckCircle2 size={16} color="#0d7a3a" strokeWidth={2.2} />}
                  {isWrongChosen && <XCircle size={16} color="#b3432f" strokeWidth={2.2} />}
                </button>
              );
            })}
          </div>

          {answerResult && answerResult.feedback_text && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, marginBottom: 12, fontSize: "0.88rem",
              background: answerResult.is_correct ? "#e6f5ec" : "#fff1f6", color: "#333",
            }}>
              {answerResult.feedback_text}
            </div>
          )}

          {!answerResult ? (
            <button onClick={submitAnswer} disabled={selectedOption == null} style={{ background: "#e5005b" }}>Ответить</button>
          ) : (
            <button onClick={nextStep} style={{ background: "#e5005b" }}>{isLastStep ? "Завершить" : "Дальше"}</button>
          )}
        </div>
      )}

      {finished && (
        <div>
          <p style={{ color: "#9c0040", fontSize: "1rem", fontWeight: 600 }}>
            Разбор пройден: {score} из {quiz.steps.length} верно
          </p>
          {stats && (
            <p style={{ color: "#b0476f", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 5 }}>
              <Users size={13} strokeWidth={2.2} /> Всего прошли: {stats.attempts_count}
            </p>
          )}
          <button onClick={restart} style={{ background: "#888" }}>Пройти ещё раз</button>
        </div>
      )}
    </div>
  );
}
